import { FileUploader } from 'react-drag-drop-files';
import { useRef, useState } from 'react';
import { Container, StyledSubTitle, StyledTitle, Wrapper } from './styles';
import { Controller, useFormContext } from 'react-hook-form';
import { RecordFilter } from '@/types/api';
import { useUploadFile } from '@/hooks/apiHooks';
import FileContent from '@/components/Record/SubjectContent/AttachmentFile/FileContent';
import UploadButton from '@/components/Record/UploadButton';
import { formatBytes } from '../../util/function';
import { useTranslation } from 'react-i18next';
import { uploadingState } from '@/recoil/atom';
import { useSetAtom } from 'jotai';

const MAX_FILE_SIZE = 500; // 500MB

const AttachmentFile = () => {
  const { t } = useTranslation();
  const { control } = useFormContext<RecordFilter>();
  const ref = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState(false);
  const setUploading = useSetAtom(uploadingState);
  const [isUploading, setIsUploading] = useState(false);
  const [openDisabled, setOpenDisabled] = useState(true);

  return (
    <Controller
      name={'attachments'}
      control={control}
      render={({ field }) => {
        const handleChange = async (newFiles: File[] | FileList | File) => {
          setIsUploading(true);
          const filesArray = Array.isArray(newFiles)
            ? newFiles
            : newFiles instanceof FileList
            ? Array.from(newFiles)
            : [newFiles];
          setUploading((prev) => ({
            ...prev,
            file: [
              ...prev.file,
              ...filesArray.map((f) => ({ name: f.name, percent: 0 })),
            ],
          }));
          let currentValues = field.value;
          for (const file of filesArray) {
            try {
              const uploadData = await useUploadFile(
                file,
                true,
                undefined,
                ({ percent }) =>
                  setUploading((prev) => ({
                    ...prev,
                    file: prev.file.map((item) =>
                      item.name === file.name ? { ...item, percent } : item,
                    ),
                  })),
              );
              const newFile = {
                name: uploadData.data.fileName,
                uuid: uploadData.data.uuid,
                size: formatBytes(uploadData.data.fileSizeInBytes),
              };
              currentValues = [...currentValues, newFile];
              field.onChange(currentValues);
            } catch (e) {
              console.error(e);
            } finally {
              setUploading((prev) => ({
                ...prev,
                file: prev.file.filter((item) => item.name !== file.name),
              }));
            }
          }
          setIsUploading(false);
        };
        return (
          <Container>
            <FileUploader
              ref={ref}
              handleChange={handleChange}
              name="file"
              multiple={true}
              dropMessageStyle={{
                backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
                border: 'none',
              }}
              maxSize={MAX_FILE_SIZE}
              onSizeError={() => setError(true)}
              onTypeError={() => setError(true)}
              disabled={openDisabled}
            >
              <Wrapper>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {field.value && field.value.length > 0 ? (
                    field.value.map((res) => (
                      <FileContent
                        key={res.uuid}
                        fileName={res.name}
                        uuid={res.uuid}
                        fileSize={res.size}
                      />
                    ))
                  ) : (
                    <StyledTitle>
                      {t('파일을 이곳에 추가해 주세요.')}
                    </StyledTitle>
                  )}
                </div>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <UploadButton
                    isUploading={isUploading}
                    title={t('업로드')}
                    style={{ width: '64px', height: '36px' }}
                    onClick={() => ref.current?.click()}
                    onMouseEnter={() => setOpenDisabled(false)}
                    onmouseleave={() => setOpenDisabled(true)}
                  />
                  <StyledSubTitle>
                    {t('파일은 개당 최대 500MB까지 첨부할 수 있어요.')}
                  </StyledSubTitle>
                </div>
              </Wrapper>
            </FileUploader>
          </Container>
        );
      }}
    />
  );
};

export default AttachmentFile;
